import * as querystring from 'querystring'

import { getAccountId } from '@patrianna/shared-utils/getUserAccountId'
import { getUserHash } from '@patrianna/shared-utils/getUserHash'
import { isReadySkipOtpSelector, skipOtpSelector } from '@patrianna-payments/shared-store/skip-otp'
import { createSelector } from '@reduxjs/toolkit'
import { differenceInDays, parseISO } from 'date-fns'

import { IS_BRAND_PULSZ } from 'src/config/brands'
import { LoyaltyStatus } from 'src/containers/PulszPointsRewards/types'
import { getItemFormLocalStorageSelector, IS_SIGN_UP_SESSION } from 'src/utils/localStorage'
import { getActiveCurrencyIdSelector } from 'store/modules/currencies/selectors'
import { isKYCConfirmedSelector, jumioKycAddressSelector } from 'store/modules/kyc/selectors'

import type {
  User,
  UserPersonalInfo,
  UserPersonalInfoFormValues,
} from '@patrianna/shared-patrianna-types/store/UserModule'
import type { ReduxState } from 'src/store/types'

const HOURS_IN_MINUTES = 60
const SECONDS_IN_MILLISECONDS = 1000

const getUserRoot = (state: ReduxState) => state?.user

export const getUserSelector = createSelector(getUserRoot, (user) => user?.info)
export const getSrVersionSelector = createSelector(getUserSelector, (user) => user?.srVersion)
export const getBaseAcceptedSrVersionSelector = createSelector(getUserSelector, (user) => user?.acceptedSrVersion)
export const getTcVersionSelector = createSelector(getUserSelector, (user) => user?.tcVersion)
export const getAcceptedTcVersionSelector = createSelector(getUserSelector, (user) => user?.acceptedTcVersion)
export const getPpVersionSelector = createSelector(getUserSelector, (user) => user?.ppVersion)
export const getAcceptedPpVersionSelector = createSelector(getUserSelector, (user) => user?.acceptedPpVersion)
export const getUserModeSelector = createSelector(getUserSelector, (user) => user?.mode)
export const getUserFlowIsRunningSelector = createSelector(getUserRoot, (user) => user.userFlowIsRunning)

export const oauthUsedSelector = createSelector(getUserSelector, (user) => user?.oauth.length > 0)

export const getUserXpLevelSelector = createSelector(getUserRoot, (user) => user.xpLevel)
export const isGuestModeSelector = createSelector(getUserRoot, (user) => user.info?.guest)
export const getUserBirthDateSelector = createSelector(getUserSelector, (user) => user?.birthDate)

export const getUserCreatedAtSelector = createSelector(getUserSelector, (user) => user?.createdAt)

export const getUserVipPointsLevelSelector = createSelector(getUserRoot, (user) => user.vipLevel)

export const getUserVipNextLevelName = createSelector(getUserVipPointsLevelSelector, (vipInfo) => {
  const VIPNames = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Royal Diamond', 'Black Diamond']

  if (VIPNames.indexOf(vipInfo?.name) === -1 || !vipInfo?.name) {
    return 'Wrong status'
  }
  if (VIPNames.indexOf(vipInfo.name) === VIPNames.length - 1) {
    return VIPNames[VIPNames.indexOf(vipInfo.name)]
  }

  return VIPNames[VIPNames.indexOf(vipInfo.name) + 1]
})

export const getUserPulszPointsLevelSelector = createSelector(getUserVipPointsLevelSelector, (vipLevel) =>
  Object.values(LoyaltyStatus).includes(vipLevel?.name as LoyaltyStatus)
    ? (vipLevel.name as LoyaltyStatus)
    : LoyaltyStatus.HERO,
)

export const needToVerifyEmailSelector = createSelector(getUserSelector, (user) => {
  if (!user) {
    return false
  }

  return user.oauth.length === 0 && !user.emailVerified
})
export const getUserPhoneNumberSelector = createSelector(getUserSelector, (user) => user?.phoneNumber || '')
export const getUserEmailSelector = createSelector(getUserSelector, (user) => user?.email || '')
export const getUserRealEmail = createSelector(getUserSelector, (user) => user?.realEmail)
export const isLoggedInSelector = createSelector(getUserSelector, (user) => !!user)
export const personalInfoSelector = createSelector(getUserSelector, (user) => user.personalInfo)
export const getUserPaymentMetaDataInfoSelector = createSelector(getUserRoot, (user) => user?.paymentMetaDataInfo)
export const getLastActivitySelector = createSelector(getUserRoot, (user) => user?.lastActivity)
export const getLoyaltyInactivityShownSelector = createSelector(getUserRoot, (user) => user?.loyaltyInactivityShown)
export const isSoftKycSentSelector = createSelector(getUserSelector, (user) => Boolean(user?.softKycInfo))
export const softKycAddressLineSelector = createSelector(getUserSelector, (user) => user?.softKycInfo?.address)
export const softKycFirstNameSelector = createSelector(getUserSelector, (user) => user?.softKycInfo?.firstName)
export const softKycLastNameSelector = createSelector(getUserSelector, (user) => user?.softKycInfo?.lastName)
export const SoftKycStateSelector = createSelector(getUserSelector, (user) => user?.softKycInfo?.state?.toUpperCase())
export const userPhoneNumberSelector = createSelector(getUserSelector, (user) => user?.phoneNumber)
export const getUserRestrictionsSelector = createSelector(getUserSelector, (user) => user?.restrictions)
export const getIsSoftKYCAvailableSelector = createSelector(getUserSelector, (user) => {
  return Boolean(
    user?.softKycInfo?.address &&
    user?.softKycInfo?.city &&
    user?.softKycInfo?.firstName &&
    user?.softKycInfo?.lastName &&
    user?.softKycInfo?.country &&
    user?.softKycInfo?.state &&
    user?.softKycInfo?.zip &&
    user?.birthDate?.length,
  )
})

export const getSoftKYCInfoSelector = createSelector(getUserSelector, (user) => user?.softKycInfo)

export const requiresJumioKYCSelector = createSelector(getUserSelector, (user) => {
  return user?.status?.includes?.('REQUIRES_JUMIO_KYC')
})

export const getPrefillDataFromSoftKyc = createSelector(
  getSoftKYCInfoSelector,
  getUserBirthDateSelector,
  getUserEmailSelector,
  getUserSelector,
  (softKyc, birthDate, email, user) => {
    const formattedBirthDate = birthDate ? new Date(birthDate) : null
    if (formattedBirthDate) {
      formattedBirthDate.setTime(
        formattedBirthDate.getTime() +
          formattedBirthDate.getTimezoneOffset() * HOURS_IN_MINUTES * SECONDS_IN_MILLISECONDS,
      )
    }

    return {
      address: softKyc?.address || '',
      address2: softKyc?.address2 || '',
      state: softKyc?.state?.toUpperCase() || '',
      firstName: softKyc?.firstName || user?.firstName || '',
      lastName: softKyc?.lastName || user?.lastName || '',
      city: softKyc?.city || '',
      country: softKyc?.country || '',
      zip: softKyc?.zip || '',
      birthDate: formattedBirthDate ? new Date(birthDate) : null,
      day: formattedBirthDate ? formattedBirthDate.getDate().toString() : '',
      month: formattedBirthDate ? (formattedBirthDate.getMonth() + 1).toString() : '',
      year: formattedBirthDate ? formattedBirthDate.getFullYear().toString() : '',
      email,
    }
  },
)

export const getUserPersonalInfoSelector = createSelector(getUserSelector, (user) => user?.personalInfo)

export const getPrefillDataFromUserPersonalInfo = createSelector(
  getUserPersonalInfoSelector,
  getUserEmailSelector,
  (user: UserPersonalInfo, email: string): UserPersonalInfoFormValues => {
    const formattedBirthDate = user?.birthDate ? new Date(user?.birthDate) : null
    if (formattedBirthDate) {
      formattedBirthDate.setTime(
        formattedBirthDate.getTime() +
          formattedBirthDate.getTimezoneOffset() * HOURS_IN_MINUTES * SECONDS_IN_MILLISECONDS,
      )
    }

    return {
      address: user?.address || '',
      address2: user?.address2 || '',
      state: user?.state?.toUpperCase() || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      city: user?.city || '',
      country: user?.country || '',
      zip: user?.zip || '',
      birthDate: formattedBirthDate || null,
      day: formattedBirthDate ? formattedBirthDate.getDate().toString() : '',
      month: formattedBirthDate ? (formattedBirthDate.getMonth() + 1).toString() : '',
      year: formattedBirthDate ? formattedBirthDate.getFullYear().toString() : '',
      email,
    }
  },
)

export const getIsPersonalInfoAddressAvailableSelector = createSelector(
  getUserPersonalInfoSelector,
  (user): boolean => {
    return Boolean(
      user?.firstName && user?.lastName && user?.address && user?.city && user?.state && user?.country && user?.zip,
    )
  },
)

export const getUserDataNameSoftKYCOrKYCInfoSelector = createSelector(
  [jumioKycAddressSelector, getUserSelector],
  (kycInfo, user) => {
    const { softKycInfo } = user || {}

    if (kycInfo?.firstName) {
      return {
        firstName: kycInfo.firstName,
        lastName: kycInfo.lastName,
      }
    }

    if (softKycInfo?.firstName) {
      return {
        firstName: softKycInfo.firstName,
        lastName: softKycInfo.lastName,
      }
    }

    return null
  },
)

export const getCurrentLevelPercentageSelector = createSelector(
  getUserXpLevelSelector,
  isLoggedInSelector,
  (xpLevel, loggedIn) => {
    const MAX_UI_LVL = 87

    if (!loggedIn || !xpLevel) {
      return 0
    }

    if (xpLevel.current === xpLevel.range || xpLevel.current === 0) {
      return 0
    }

    const currentPosition = -(xpLevel.threshold - xpLevel.range - xpLevel.current) / xpLevel.range

    const progress = currentPosition * MAX_UI_LVL

    return Math.round(progress)
  },
)

export const getCurrentVIPLevelPercentageSelector = createSelector(
  getUserVipPointsLevelSelector,
  isLoggedInSelector,
  (xpLevel, loggedIn) => {
    if (!loggedIn || !xpLevel) {
      return 0
    }

    if (xpLevel.current === xpLevel.range || xpLevel.current === 0) {
      return 0
    }

    if (xpLevel.threshold - xpLevel.range < xpLevel.current) {
      const currentPosition = -(xpLevel.threshold - xpLevel.range - xpLevel.current) / xpLevel.range

      const progress = currentPosition * 100

      return Math.round(progress)
    } else {
      const currentPosition = xpLevel.current / xpLevel.threshold

      const progress = currentPosition * 100

      return Math.round(progress)
    }
  },
)
// @TODO replace selector by getUserCustomerIdSelector
export const getUserIdSelector = createSelector(getUserSelector, (user) => user?.id)
export const getUserAccountIdSelector = createSelector(getUserSelector, (user) => getAccountId(user))
export const getUserHashSelector = createSelector(getUserSelector, (user) => getUserHash(user))
export const getUserCustomerIdSelector = createSelector(getUserSelector, (user) => user?.customerId)

export const getGoldenKeySelector = createSelector(getUserPaymentMetaDataInfoSelector, (metaData) => {
  if (metaData?.lastDeposit) {
    return differenceInDays(new Date(), parseISO(metaData.lastDeposit)) <= 6
  }

  return false
})

export const getUserSubscriptionsSelector = createSelector(getUserSelector, (user) =>
  user?.subscriptions?.length ? user?.subscriptions : null,
)

export const getUserRealEmailSelector = createSelector(getUserRealEmail, (email) => {
  return email
})

export const emailHiddenSelector = createSelector(
  getUserSelector,
  (user) => user.realEmail.includes('onlinelotto.com') || user.realEmail.includes('pulsz.com'),
)

export const facebookConnectedSelector = createSelector(
  getUserSelector,
  isLoggedInSelector,
  (user, loggedIn) => loggedIn && user.oauth.includes('facebook'),
)

export const getUserInvitationInfoSelector = createSelector(getUserSelector, (user) => user?.invitation)

export const getSentWelcomeEmailFieldSelector = createSelector(getUserSelector, (user) => {
  return user?.sentWelcomeEmail
})

export const getIsAdminUserSelector = createSelector(getUserSelector, (user) => user?.admin)

export const getUserFirstPurchaseSelector = createSelector(
  getUserRoot,
  (user) => user?.paymentMetaDataInfo?.firstDeposit,
)
export const getPurchaseVipStatusSelector = createSelector(getUserSelector, (user) => user?.purchaseVipLevel)

export const getUserIfLastDepositDurationThirtyDays = createSelector(getUserPaymentMetaDataInfoSelector, (metaData) => {
  if (metaData?.lastDeposit) {
    return differenceInDays(new Date(), parseISO(metaData.lastDeposit)) <= 29
  }

  return false
})

export const getUserCreatedInTheLastThirtyDays = createSelector(getUserSelector, (user) => {
  if (user?.createdAt) {
    return differenceInDays(new Date(), parseISO(user.createdAt)) <= 29
  }

  return false
})

export const getGraphyteEnabledSelector = createSelector(
  getUserIfLastDepositDurationThirtyDays,
  getUserCreatedInTheLastThirtyDays,
  (deposit, createdAt) => deposit || createdAt,
)

export const getGraphyteEnabledSCSelector = createSelector(
  getUserIfLastDepositDurationThirtyDays,
  getUserCreatedInTheLastThirtyDays,
  getActiveCurrencyIdSelector,
  (deposit, createdAt, activeCurrency) => (deposit || createdAt) && activeCurrency === 'SC',
)

export const isLoyaltyInactivityAvailableSelector = createSelector(
  getLastActivitySelector,
  getLoyaltyInactivityShownSelector,
  (lastActivity, shown) => {
    if (!IS_BRAND_PULSZ || shown || !lastActivity) {
      return false
    }
    const diff = differenceInDays(new Date(), parseISO(lastActivity))

    return diff >= 7
  },
)

export const srCheckVersionSelector = createSelector(
  getSrVersionSelector,
  getBaseAcceptedSrVersionSelector,
  (srVersion, acceptedSrVersion) => srVersion === acceptedSrVersion,
)

export const tcCheckVersionSelector = createSelector(
  getTcVersionSelector,
  getAcceptedTcVersionSelector,
  (tcVersion, acceptedTcVersion) => tcVersion === acceptedTcVersion,
)

export const ppCheckVersionSelector = createSelector(
  getPpVersionSelector,
  getAcceptedPpVersionSelector,
  (ppVersion, acceptedPpVersion) => ppVersion === acceptedPpVersion,
)

export const getAcceptedSrVersionSelector = createSelector(getBaseAcceptedSrVersionSelector, (acceptedSrVersion) =>
  Number(acceptedSrVersion),
)

export const getUserLegalRulesSelector = createSelector(getUserSelector, (user) => user?.legalRules)

export const skipUpdateLegalsScEnabledSelector = createSelector(
  srCheckVersionSelector,
  ppCheckVersionSelector,
  tcCheckVersionSelector,
  getAcceptedSrVersionSelector,
  getUserLegalRulesSelector,
  (isAcceptedSr, isAcceptedPp, isAcceptedTc, acceptedSrVersion, legalRules) => {
    return !legalRules || !acceptedSrVersion || (isAcceptedSr && isAcceptedTc && isAcceptedPp)
  },
)

export const skipUpdateLegalsSCDisabledSelector = createSelector(
  getUserLegalRulesSelector,
  ppCheckVersionSelector,
  tcCheckVersionSelector,
  (legalRules, isAcceptedPp, isAcceptedTc) => {
    return !legalRules || (isAcceptedTc && isAcceptedPp)
  },
)

export const isUpdateingLegalsSelector = createSelector(getUserRoot, (user) => user.isUpdateingLegals)

export const showOtpPageSelector = createSelector(
  skipOtpSelector,
  needToVerifyEmailSelector,
  getUserSelector,
  isReadySkipOtpSelector,
  (skipOtp, emailNotVerified, user, isReadySkipOtp) =>
    user && !emailNotVerified && !skipOtp && !user.locked && isReadySkipOtp,
)

export const getLiveChatSettingsSelector = createSelector(getUserRoot, (user) => user?.liveChatSettings)

export const getLiveChatUserInfoSelector = createSelector(
  getUserSelector,
  getLiveChatSettingsSelector,
  (user, liveChatSettings) => ({ ...user, email: liveChatSettings?.email, id: liveChatSettings?.external_id }) as User,
)

export const getLiveChatDepartmentsSelector = createSelector(
  getLiveChatSettingsSelector,
  (liveChatSettings) => liveChatSettings?.departments,
)

export const getLiveChatVipSelector = createSelector(
  getLiveChatSettingsSelector,
  (liveChatSettings) => liveChatSettings?.vip,
)

export const getLiveChatInfoSelector = createSelector(
  getLiveChatUserInfoSelector,
  getLiveChatVipSelector,
  (user, liveChatVip) =>
    ({
      ...user,
      isLiveChatVip: liveChatVip,
    }) as User,
)

export const getLiveChatVisibleSelector = createSelector(
  getLiveChatSettingsSelector,
  (_: ReduxState, isGamePage: boolean) => isGamePage,
  (liveChatSettings, isGamePage) => liveChatSettings?.showLiveChat && IS_BRAND_PULSZ && !isGamePage,
)

export const getUserCommunicationPreferencesSelector = createSelector(getUserSelector, (user) => ({
  doNotSendEmails: user?.doNotSendEmails ?? false,
  doNotSendPushes: user?.doNotSendPushes ?? false,
  doNotSendSms: user?.doNotSendSms ?? true,
  doNotCall: user?.doNotCall ?? true,
}))

export const getUserLegalRulesTypeSelector = createSelector(getUserSelector, (user) =>
  user?.legalRules?.map((el) => el.type),
)

export const getUserLegalRulesDateSelector = createSelector(getUserSelector, (user) => {
  return user?.legalRuleDate
    ? new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(new Date(user.legalRuleDate))
    : null
})

export const getUserLegalRulesModalSettingSelector = createSelector(
  getUserSelector,
  getUserLegalRulesSelector,
  (user, legalRules) => {
    if (!user || !legalRules) {
      return null
    }

    const settingsPriority: Record<string, number> = {
      Tickbox: 0,
      Popup: 1,
      Nothing: 2,
    }

    const setModalSetting = legalRules.reduce((result, item) => {
      if (!result || settingsPriority[item.setting] < settingsPriority[result.setting]) {
        return item
      }

      return result
    })

    return setModalSetting.setting
  },
)

export const getUserKYCFullName = createSelector(getUserPersonalInfoSelector, (info) =>
  info ? `${info.firstName} ${info.lastName}` : '',
)

export const getUserZendeskQueryString = createSelector(
  getUserKYCFullName,
  getUserEmailSelector,
  getUserCustomerIdSelector,
  (fullName, email, customerId): string => {
    if (fullName && email && customerId) {
      return querystring.stringify({
        tf_anonymous_requester_email: email,
        tf_360013443077: fullName,
        tf_360013482117: customerId,
      })
    }

    return ''
  },
)

export const needToVerifyJumioKYCSelector = createSelector(getUserSelector, (user) => {
  if (!user) {
    return false
  }

  return user.status === 'RESTRICTED_BY_GEO_POLICY'
})

export const shouldForceVerifyKYCSelector = createSelector(
  isKYCConfirmedSelector,
  needToVerifyJumioKYCSelector,
  needToVerifyEmailSelector,
  (isKYCConfirmed, needToVerifyJumioKYC, needToVerifyEmail) => {
    const needToVerify = needToVerifyJumioKYC && !needToVerifyEmail

    return needToVerify && !isKYCConfirmed
  },
)

export const socialAuthDataSelector = createSelector(getUserRoot, (user) => user.socialAuthData)

export const getCurrencyDataFromUserPersonalInfoSelector = createSelector(
  getUserPersonalInfoSelector,
  (personalInfo): UserPersonalInfo | { currency: 'USD'; country: 'US'; state: null } => {
    return personalInfo || { currency: 'USD', country: 'US', state: null }
  },
)

export const getUserPasswordChanged = (state: ReduxState) => state.user.userPasswordChanged

export const getUserPasswordChangedSelector = createSelector(
  getUserPasswordChanged,
  (userPasswordChanged) => userPasswordChanged,
)

export const getTabsConfig = (state: ReduxState) => state.user.tabsConfig

export const getTabsConfigSelector = createSelector(getTabsConfig, (config) => config)

export const getUserLocationStatus = createSelector(getUserSelector, (user) => user?.locationStatus)
export const getIsAdminOnlyPromoContentSelector = createSelector(
  [getIsAdminUserSelector],
  (isAdmin) =>
    (adminOnly?: boolean): boolean => {
      const isAdminOnlyContent = adminOnly ?? false

      return isAdminOnlyContent && !isAdmin
    },
)

export const getUserEmailVerifiedSelector = createSelector(getUserSelector, (user) => user?.emailVerified ?? false)

export const getFraudCheckCompletedSelector = createSelector(getUserRoot, (user) => user?.fraudCheckCompleted)
export const getGeoVerificationRequiredSelector = createSelector(getUserRoot, (user) => user?.geoVerificationRequired)
export const isSignUpSessionSelector = (state: ReduxState) =>
  getItemFormLocalStorageSelector(state, IS_SIGN_UP_SESSION, false)
